import React from "react";
import { BarChart3, LayoutDashboard, Users, GitCompareArrows, Eye, TrendingUp, MousePointerClick, Target, Activity, Lock } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, StatBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { RankingsMock, KpiMock, FunnelMock, ProfileFieldsMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1723987251277-18fc0a1effd0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function AnalyticsFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-analytics-page">
      <FeatureHero
        eyebrow="Analytics"
        icon={BarChart3}
        title="Your presence is doing things. See what."
        copy="Startup Otter Analytics helps qualified members understand how people discover, engage with, and act on the Profiles, Businesses, Capital organizations, Events, and Resources they manage."
        ctaLabel="Explore Analytics"
        ctaTo="/pricing"
        visual={<RankingsMock testId="analytics-hero-visual" />}
      />

      <FeatureRow
        testId="analytics-summary"
        label="Summary"
        icon={LayoutDashboard}
        title="One Analytics Summary."
        copy="See performance across everything you manage before diving deeper into individual dashboards."
        visual={<KpiMock testId="mock-analytics-summary" items={[{ label: "Unique audience", value: "842", delta: "+7%" }, { label: "Total entity visits", value: "1,914", delta: "+12%" }, { label: "Total engagement", value: "268", delta: "+4%" }, { label: "Search impressions", value: "3,406", delta: "+15%" }]} />}
      />

      <IconGrid
        testId="analytics-dashboards"
        tone="tint"
        columns={3}
        title="Then go deeper."
        intro="Explore dedicated analytics for Personal Profiles, Businesses, Capital Profiles, Events, and Resources."
        items={[
          { icon: Users, title: "People", copy: "How your personal profile is found, followed and asked about." },
          { icon: LayoutDashboard, title: "Businesses & Capital", copy: "Audience, inbound interest and the actions people take next." },
          { icon: Activity, title: "Events & Resources", copy: "Views, interest, visits, shares and community ratings." },
        ]}
      />

      <FeatureRow
        testId="analytics-reach-depth"
        tone="dark"
        flip
        label="Reach vs. depth"
        icon={GitCompareArrows}
        title="Two numbers that mean different things."
        copy="Unique Audience shows how many individual people you reached. Total Entity Visits shows how deeply they explored your Startup Otter presence."
        visual={
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="mock-analytics-reach">
            {[["Unique audience", "842", "individual people"], ["Total entity visits", "1,914", "visits across your presence"]].map(([label, value, sub]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <div className="text-[10px] font-[700] uppercase tracking-[0.1em] text-teal-300">{label}</div>
                <div className="mt-3 text-4xl font-[700] text-white">{value}</div>
                <div className="mt-1 text-xs text-white/60">{sub}</div>
              </div>
            ))}
          </div>
        }
      />

      <FeatureRow
        testId="analytics-comparisons"
        label="Comparisons"
        icon={TrendingUp}
        title="Know what's changing."
        copy="Compare percentage and absolute performance changes against the previous matching period."
        visual={<FunnelMock testId="mock-analytics-funnel" />}
      />

      <IconGrid
        testId="analytics-rankings"
        tone="brand"
        columns={4}
        title="Find your standouts."
        items={[
          { icon: Eye, title: "Most Viewed", copy: "What attracts the most attention." },
          { icon: TrendingUp, title: "Fastest Growing", copy: "What's gaining momentum." },
          { icon: MousePointerClick, title: "Most Engaged", copy: "What inspires people to interact." },
          { icon: Target, title: "Most Converted", copy: "What turns attention into meaningful action." },
        ]}
      />

      <PhotoBand
        testId="analytics-actions"
        src={PHOTO}
        alt="A team reviewing charts on screen"
        kicker="Signals"
        title="Measure more than views."
        copy="Depending on the entity, track signals such as visits, search exposure, follows, Fans, bookmarks, meeting requests, questions, shares, ratings, and more."
      />

      <FeatureRow
        testId="analytics-signals"
        tone="tint"
        label="Actions"
        icon={Activity}
        title="The actions that actually matter."
        copy="Follows and Fans, bookmarks, meeting requests and questions — the things that mean someone did something about you."
        visual={<KpiMock testId="mock-analytics-actions" items={[{ label: "Followers gained", value: "36", delta: "+9%" }, { label: "Fans gained", value: "12", delta: "+3" }, { label: "Bookmarks added", value: "44", delta: "+8" }, { label: "Meetings requested", value: "17", delta: "+4" }]} />}
      />

      <FeatureRow
        testId="analytics-capital-insights"
        flip
        label="Capital insights"
        icon={Lock}
        title="Deeper insight where appropriate."
        copy="Eligible Capital users can access identified visitor insights on supported analytics while respecting Startup Otter's privacy controls and private browsing."
        visual={<ProfileFieldsMock title="Privacy first" testId="mock-analytics-privacy" items={[["Named visitors", "Available only to eligible Capital users, on supported dashboards."], ["Private browsing", "Members who browse privately are never identified."], ["Small audiences", "Breakdowns stay hidden until there are enough visitors."]]} />}
      />

      <StatBand
        testId="analytics-stats"
        tone="dark"
        items={[
          { value: "6 dashboards", label: "Summary plus Personal, Business, Capital, Events and Resources." },
          { value: "Period over period", label: "Every number carries its change against the last matching window." },
          { value: "Privacy respected", label: "Private browsing and small-audience thresholds are always honoured." },
        ]}
      />

      <ClosingCta
        testId="analytics-closing"
        title="Views are nice. Understanding is better."
        copy="See not only whether people noticed you—but what they did next."
        ctaLabel="Explore Analytics"
        ctaTo="/pricing"
      />

      <FeatureFooterNav next={{ to: "/features/groups", label: "Groups", desc: "Give your community a home — with streams, members, announcements and conversation." }} />
    </div>
  );
}
